/**
 * 没入 P5「声を取り戻した付喪神図鑑」: 周回をまたいで端末に貯まるコレクション保存。
 * P9-1「欠けの可視化とデータ堅牢化」: runId 冪等ガード + endingTypes 集合化を追加。
 *
 * 公開 Player は ticketless（サーバー保存先がない）ため、保存先は localStorage で端末ローカル完結（MVP）。
 * 既存の localStorage 規約に倣う（`player_device_key` / `player_progress_<id>` / `mg-haptics-enabled`）。
 *
 * 設計:
 * - キー `player_collection` に単一 JSON 配列を格納する。
 * - 1 シナリオ = 1 付喪神 を想定。周回は upsert（clearCount++ / lastClearedAt 更新）。
 * - エントリの一意キー `key` は scenarioId があればそれを、無ければ付喪神 name を採用する
 *   （同じ付喪神を別シナリオ id 無しで複数回クリアしても 1 エントリに集約される）。
 * - 冪等ガード（P9-1）: `recordTsukumogami` に runId（プレイスルー毎に一意な id）を渡すと、
 *   entry 側 `recordedRunIds`（直近 MAX_RECORDED_RUN_IDS 件）で同一 run の二重カウントを防ぐ。
 *   既記録 runId での再記録は**完全 no-op**（clearCount も endingTypes も動かさない）。
 *   StrictMode の二重 useEffect による「1到達で2周」を構造的に遮断し、P9-2 のサーバ台帳でも
 *   同じ runId が冪等キーになる（先行投資）。
 * - endingTypes（P9-1）: 到達済み結末の集合（重複なし・到達順）。旧形式の `endingType`（単数・
 *   最新上書き）は読み時マイグレーションで endingTypes へ吸収する（書き込みは次回 record 時）。
 * - SSR / 非ブラウザ安全（`typeof window` ガード）・parse 失敗時は空配列フォールバック・全 IO を try/catch で保護。
 */

const STORAGE_KEY = 'player_collection';

/** 冪等ガード用に保持する runId の上限。直近 N 件で十分（古いものから捨てる）。 */
const MAX_RECORDED_RUN_IDS = 20;

export type TsukumogamiRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

/** 図鑑に格納される 1 付喪神のエントリ。 */
export interface CollectionEntry {
  /** 一意キー（scenarioId 優先・無ければ name）。upsert の突き合わせに使う。 */
  key: string;
  /** 付喪神名（例「そま（火）」）。 */
  name: string;
  /** 一文の説明（任意）。 */
  description?: string;
  /** イラストのアセット ID（任意。無ければ UI はプレースホルダ表示）。 */
  illustrationAssetId?: string;
  /** レアリティ（任意）。 */
  rarity?: TsukumogamiRarity;
  /** 由来シナリオの ID（あれば）。 */
  scenarioId?: string;
  /** 由来シナリオの表示タイトル（あれば）。 */
  scenarioTitle?: string;
  /** 到達済みエンディング種別の集合（重複なし・到達順）。旧 `endingType` は読み時に吸収済み。 */
  endingTypes: string[];
  /** 記録済み runId（直近 MAX_RECORDED_RUN_IDS 件）。同一 run の二重カウント防止（P9-1）。 */
  recordedRunIds: string[];
  /** 初回クリア時刻（epoch ms）。 */
  firstClearedAt: number;
  /** 最終クリア時刻（epoch ms）。周回のたびに更新。 */
  lastClearedAt: number;
  /** 周回数（クリア回数）。 */
  clearCount: number;
}

/**
 * recordTsukumogami の実行結果（P9-2）。
 * 同期層（collectionSync.ts）が「実際にカウントが動いた記録だけ」を送信キューへ積むために使う
 * （冪等 no-op を送らない＝サーバ台帳の二重計上をクライアント側でも構造遮断）。
 */
export interface RecordTsukumogamiResult {
  /** この呼び出しで実際にカウントが動いたか（冪等 no-op / 入力不正 / 非ブラウザは false）。 */
  recorded: boolean;
  /** 記録先エントリの一意キー（resolveKey 規約）。入力不正・非ブラウザは null。 */
  key: string | null;
  /** 記録時刻 epoch ms（recorded=true のときのみ）。 */
  recordedAt: number | null;
}

/** recordTsukumogami の入力。`firstClearedAt` 等の時刻・回数はこの util が付与する。 */
export interface RecordTsukumogamiInput {
  name: string;
  description?: string;
  illustrationAssetId?: string;
  rarity?: TsukumogamiRarity;
  scenarioId?: string;
  scenarioTitle?: string;
  endingType?: string;
  /**
   * このクリアが属する run/playthrough の一意 id（冪等キー・P9-1）。
   * 同じ id での再記録は完全 no-op になる。未指定なら従来通り毎回カウントする。
   */
  runId?: string;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/** 一意キーの決定: scenarioId 優先・無ければ name。どちらも空にはならない（name は必須）。 */
function resolveKey(input: { scenarioId?: string; name: string }): string {
  const sid = (input.scenarioId ?? '').trim();
  if (sid) return `scenario:${sid}`;
  return `name:${input.name.trim()}`;
}

/**
 * 読み時マイグレーション + サニタイズ（P9-1）。
 * - 旧形式 `endingType`（単数文字列）は `endingTypes` 集合へ吸収し、旧フィールドは落とす
 *   （localStorage 内部形式・読み手はこの util のみ）。
 * - endingTypes / recordedRunIds は「文字列だけの配列」に正規化（破損・型違いは捨てる）。
 */
function migrateEntry(raw: CollectionEntry & { endingType?: unknown }): CollectionEntry {
  const { endingType: legacyEndingType, ...rest } = raw;
  const fromArray = Array.isArray(raw.endingTypes)
    ? raw.endingTypes.filter((t): t is string => typeof t === 'string' && t.length > 0)
    : [];
  const fromLegacy =
    typeof legacyEndingType === 'string' && legacyEndingType.length > 0 ? [legacyEndingType] : [];
  const endingTypes = Array.from(new Set([...fromArray, ...fromLegacy]));
  const recordedRunIds = Array.isArray(raw.recordedRunIds)
    ? raw.recordedRunIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  return { ...rest, endingTypes, recordedRunIds };
}

/** localStorage から図鑑を読む。非ブラウザ・未保存・破損時は空配列を返す。 */
export function getCollection(): CollectionEntry[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // 最低限のサニタイズ: name と key を持つオブジェクトだけ通す（破損エントリを除外）。
    return parsed
      .filter(
        (e): e is CollectionEntry =>
          e != null &&
          typeof e === 'object' &&
          typeof (e as CollectionEntry).key === 'string' &&
          typeof (e as CollectionEntry).name === 'string'
      )
      .map(migrateEntry);
  } catch {
    // parse 失敗 / アクセス不可（プライベートブラウズ等）はフォールバックで空配列。
    return [];
  }
}

function writeCollection(entries: CollectionEntry[]): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // 容量超過 / アクセス不可は黙って諦める（図鑑は補助機能なので致命ではない）。
  }
}

/**
 * P9-2 同期層（collectionSync.ts）専用: サーバ台帳とのマージ結果でローカルキャッシュを
 * 丸ごと置き換える。通常の記録は recordTsukumogami を使うこと（この関数は upsert 規約を通らない）。
 */
export function replaceCollection(entries: CollectionEntry[]): void {
  writeCollection(entries);
}

/**
 * 付喪神を図鑑へ upsert する。
 * - 既存（同 key）があれば: clearCount++ / lastClearedAt 更新 / 説明等は最新で上書き /
 *   endingType は endingTypes 集合へ追加（重複なし） / firstClearedAt は保持。
 * - 無ければ: 新規エントリを末尾に追加（firstClearedAt = lastClearedAt = now / clearCount = 1）。
 * - runId 付きで、既存エントリの recordedRunIds に同じ id があれば**完全 no-op**（冪等・P9-1）。
 * 時刻は `Date.now()`（アプリ実行時の Date 使用は問題なし）。
 * 返り値（P9-2）: 実際にカウントが動いたかどうか。同期層が送信キュー積み判定に使う。
 * 既存呼び出し側は返り値を無視してよい（従来 void からの additive な変更）。
 */
export function recordTsukumogami(input: RecordTsukumogamiInput): RecordTsukumogamiResult {
  const noop: RecordTsukumogamiResult = { recorded: false, key: null, recordedAt: null };
  if (!isBrowser()) return noop;
  const name = (input.name ?? '').trim();
  if (!name) return noop; // name は必須。空なら記録しない（捏造しない）。

  const now = Date.now();
  const key = resolveKey({ scenarioId: input.scenarioId, name });
  const runId = (input.runId ?? '').trim();
  const entries = getCollection();
  const idx = entries.findIndex((e) => e.key === key);

  if (idx >= 0) {
    const prev = entries[idx];
    // 冪等ガード（P9-1）: 同一 run の再記録（StrictMode 二重 effect / ending 画面の開き直し）は
    // 完全 no-op。clearCount / endingTypes / lastClearedAt のいずれも動かさない。
    if (runId && prev.recordedRunIds.includes(runId)) {
      return { recorded: false, key, recordedAt: null };
    }

    const endingTypes =
      input.endingType && !prev.endingTypes.includes(input.endingType)
        ? [...prev.endingTypes, input.endingType]
        : prev.endingTypes;
    const recordedRunIds = runId
      ? [...prev.recordedRunIds, runId].slice(-MAX_RECORDED_RUN_IDS)
      : prev.recordedRunIds;

    entries[idx] = {
      ...prev,
      // 周回で最新化する項目
      name,
      description: input.description ?? prev.description,
      illustrationAssetId: input.illustrationAssetId ?? prev.illustrationAssetId,
      rarity: input.rarity ?? prev.rarity,
      scenarioId: input.scenarioId ?? prev.scenarioId,
      scenarioTitle: input.scenarioTitle ?? prev.scenarioTitle,
      endingTypes,
      recordedRunIds,
      lastClearedAt: now,
      clearCount: (prev.clearCount ?? 0) + 1
      // firstClearedAt は保持（初回クリアの記録）
    };
  } else {
    entries.push({
      key,
      name,
      description: input.description,
      illustrationAssetId: input.illustrationAssetId,
      rarity: input.rarity,
      scenarioId: input.scenarioId,
      scenarioTitle: input.scenarioTitle,
      endingTypes: input.endingType ? [input.endingType] : [],
      recordedRunIds: runId ? [runId] : [],
      firstClearedAt: now,
      lastClearedAt: now,
      clearCount: 1
    });
  }

  writeCollection(entries);
  // writeCollection が容量超過等で失敗しても recorded=true を返す（ローカルキャッシュは
  // 落ちてもサーバ台帳への送信は成立させたい＝台帳の方が永続層として上位・P9-2）。
  return { recorded: true, key, recordedAt: now };
}

/** 図鑑を空にする（開発用・任意）。 */
export function clearCollection(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
